import LexoRank from '@kayron013/lexorank';
import { Injectable } from '@angular/core';
import { SchedulerItem } from '../models/item';
import { SchedulerItemOnMachine } from '../models/item-on-machine';
import { SchedulerItemOnMachineCRUD } from '../../../api/backend/dto/scheduler-item-on-machine.dto';
import { DragAndDropEvent } from '../../drag-drop/types/event';
import { SchedulerDragDropAbstract } from './drag-drop';
import { orderByRankWithMachine } from '../utils/order-by-rank';

@Injectable()
export class SchedulerMachineDragDropService<
  Type extends SchedulerItem
> extends SchedulerDragDropAbstract<Type> {
  private changeRank(
    list: Type[],
    currentIndex: number,
    previousIndex: number,
    machineId: number
  ): string {
    let newRank: LexoRank;
    if (currentIndex > list.length - 1) {
      currentIndex = list.length - 1;
    }
    if (list.length === 1) {
      return null;
    }
    if (!list[currentIndex].getItemOnMachineByMachineId(machineId)?.rank) {
      return null;
    }
    let split = list[currentIndex]
      .getItemOnMachineByMachineId(machineId)
      .rank?.split('|');
    const oldRank = new LexoRank(split[1], split[0]);
    if (currentIndex === 0) {
      newRank = oldRank.decrement();
    } else if (currentIndex === list.length - 1) {
      newRank = oldRank.increment();
    } else {
      let indexForBetween =
        currentIndex > previousIndex ? currentIndex + 1 : currentIndex - 1;
      indexForBetween = indexForBetween > 0 ? indexForBetween : 0;
      let betweenRank =
        list[indexForBetween].getItemOnMachineByMachineId(machineId)?.rank;
      const previousRank =
        list[previousIndex].getItemOnMachineByMachineId(machineId)?.rank;
      if (previousRank === betweenRank) {
        betweenRank =
          list[currentIndex + 1].getItemOnMachineByMachineId(machineId)?.rank;
      }
      if (!betweenRank) {
        return oldRank.decrement().toString();
      }
      split = betweenRank.split('|');

      const oldRankHigher = new LexoRank(split[1], split[0]);
      newRank =
        currentIndex > previousIndex
          ? LexoRank.between(oldRank, oldRankHigher)
          : LexoRank.between(oldRankHigher, oldRank);
    }
    return newRank.toString();
  }

  async success(event: DragAndDropEvent<Type>): Promise<Type[]> {
    return this.newVersionsOfLists(event, (val) => {
      return orderByRankWithMachine(
        val,
        this.generateMachineId(event.container.id)
      );
    });
  }

  async saveChanges(event: DragAndDropEvent<Type>): Promise<void> {
    // we change the rank of item that was moved.
    const isItemOnMachine = event.previousContainer.id.includes('machine');
    const itemOnMachine = isItemOnMachine
      ? await this.insertingItemWhenIsFromMachine(event)
      : await this.insertingItemWhenNotFromMachine(event);

    const itemToMove = event.item.data;
    if (itemOnMachine && itemOnMachine.id) {
      itemToMove.itemsOnMachine = [];
      // It's important that we create a new class for the item
      itemToMove.factoryItemOnMachine(itemOnMachine);
    }
    if (!isItemOnMachine) {
      this.containerService.notifyItemMovedToMachine(itemToMove);
    }
  }

  protected async insertingItemWhenIsFromMachine(
    event: DragAndDropEvent<Type>
  ): Promise<SchedulerItemOnMachine> {
    const itemToMove = event.item.data;
    const machineId = this.generateMachineId(event.container.id);
    const previousMachineId = this.generateMachineId(
      event.previousContainer.id
    );

    const rank = this.createNewRank(event, machineId);

    const body: SchedulerItemOnMachineCRUD['update'] = {
      newMachineId: machineId,
      // In order to get the id of the previous machine we need to check the id of the previous container
      previousMachineId,
      isCurrent: event.container.id.includes('action'),
      itemId: itemToMove.id,
      newRankMachine: this.allowedToBeOrdered && !rank ? itemToMove.rank : rank,
    };
    return await this.apiRouter
      .updateItem(body)
      .toPromise()
      .then((newItem) => {
        return newItem;
      });
  }

  protected async insertingItemWhenNotFromMachine(
    event: DragAndDropEvent<Type>
  ): Promise<SchedulerItemOnMachine> {
    const itemToMove = event.item.data;
    const machineId = this.generateMachineId(event.container.id);
    const rank = this.createNewRank(event, machineId);
    const body: SchedulerItemOnMachineCRUD['add'] = {
      itemId: itemToMove.id,
      machineId,
      isCurrent: event.container.id.includes('action'),
      isRunning: false,
      rank: rank || itemToMove.rank,
    };
    return await this.apiRouter.addItem(body).toPromise();
  }

  /**
   * This method has the main objetive of obtaining the new rank that the item will have
   * @param event
   * @param machineId
   * @protected
   */
  protected createNewRank(
    event: DragAndDropEvent<Type>,
    machineId: number
  ): string {
    if (!this.allowedToBeOrdered || !this.authService.orderingItems()) {
      return null;
    }
    const newList =
      event.container.id !== event.previousContainer.id
        ? [...event.container.data, event.item.data]
        : [...event.container.data];

    return this.changeRank(
      newList,
      event.currentIndex,
      event.container.id !== event.previousContainer.id
        ? newList.length - 1
        : event.previousIndex,
      machineId
    );
  }

  protected generateMachineId(containerId: string): number {
    const list = containerId.split('-');
    return parseInt(list[list.length - 1], 10);
  }
}
